// ── A qué elemento se le pega el clic ────────────────────────────────────────
//
// Al hacer clic, la selección sube al ancestro más cercano con data-evo-id. Si
// se le pega justo al texto, ese ancestro es el texto y todo bien. Si se le pega
// al padding de una card, es la card: un contenedor cuyo texto pertenece a sus
// hijos, así que queda marcado con candado y no pasa nada.
//
// Medido sobre tres páginas reales: entre 19 y 23 contenedores por página tienen
// UN SOLO descendiente editable. En esos no hay ambigüedad posible sobre qué
// texto se quiso tocar, así que el clic se redirige ahí. No cambia qué es
// editable — cambia cuánta puntería hace falta.
//
// Solo aplica a contenedores. Un elemento con texto propio partido en trozos
// (kind 'split-text') puede tener un hijo editable adentro, y redirigir ahí
// editaría en silencio algo distinto de lo que se señaló.

/** Lo que hace falta saber del elemento, sin depender de cómo se clasifica. */
export interface EditTargetProbe {
  esEditable: (el: Element) => boolean;
  esContenedor: (el: Element) => boolean;
}

/**
 * Único descendiente editable, o null si hay cero o más de uno.
 *
 * Con más de uno no se puede adivinar cuál se quiso: se deja el candado y el
 * panel explica que hay que hacer clic sobre el texto.
 */
export function soleEditableDescendant(
  el: Element,
  esEditable: (e: Element) => boolean,
): Element | null {
  let encontrado: Element | null = null;
  for (const cand of Array.from(el.querySelectorAll('[data-evo-id]'))) {
    if (!esEditable(cand)) continue;
    if (encontrado) return null; // más de uno: ambiguo
    encontrado = cand;
  }
  return encontrado;
}

/**
 * Elemento sobre el que hay que actuar: el mismo, o el único texto que contiene.
 *
 * Se usa para el clic Y para la marca de hover, así que lo que se ve resaltado
 * es siempre lo que se va a editar.
 */
export function resolveEditTarget(el: Element, probe: EditTargetProbe): Element {
  if (probe.esEditable(el)) return el;
  if (!probe.esContenedor(el)) return el;
  return soleEditableDescendant(el, probe.esEditable) ?? el;
}

// ── El ícono pertenece a su recuadro ────────────────────────────────────────
//
// El círculo con la flecha de un botón es un <span> de 44x44 con fondo y borde
// redondeado; adentro tiene un <svg> de 20x20 centrado. Clickear el círculo marca
// el svg, y el margen del círculo es del span: había que subir un escalón a mano.
//
// Medido: el hijo NO llena al contenedor —20x20 contra 44x44—, así que la
// geometría no sirve para decidirlo. Lo que distingue al caso es otra cosa: lo
// que se clickeó no tiene texto, y su contenedor no contiene nada más. Ahí el
// ícono y su recuadro son la misma cosa para quien mira, y el recuadro es el que
// tiene el estilo.

/** Un escalón de la cadena: el elemento clickeado y después sus ancestros. */
export interface Escalon {
  targetId: string;
  tag: string;
  /** Texto propio, ya recortado. */
  texto: string;
  /** Cuántos hijos ELEMENTO tiene. Con más de uno, subir se llevaría a los hermanos. */
  hijosElemento: number;
}

/**
 * Envoltorios que NO se prefieren aunque cumplan lo demás: son la acción, no el
 * adorno. Clickear el ícono de un botón y terminar marcando el botón entero
 * cambia de qué se está hablando.
 */
const NO_SUBIR_A = new Set(['a', 'button', 'label', 'summary']);

/**
 * Hasta dónde subir. Devuelve el índice de la cadena que hay que marcar.
 *
 * Un solo escalón, y solo cuando:
 *   - lo clickeado no tiene texto propio (es un ícono, un adorno),
 *   - el contenedor no tiene otros hijos ni texto propio,
 *   - el contenedor no es el elemento interactivo que lo envuelve.
 *
 * Un escalón alcanza para el caso real y no deja que marcar algo termine
 * marcando media página.
 */
export function contenedorDelIcono(cadena: Escalon[]): number {
  const hijo = cadena[0];
  const padre = cadena[1];
  if (!hijo || !padre) return 0;
  if (hijo.texto !== '') return 0;
  if (padre.texto !== '') return 0;
  if (padre.hijosElemento !== 1) return 0;
  if (NO_SUBIR_A.has(padre.tag)) return 0;
  return 1;
}
